// @flow
import * as React from 'react';
import {
  PowerReviewsModerationCard,
  PowerReviewsKeyboardEnchancer,
} from 'components';
import { connect } from 'react-redux';
import {
  requestContent,
  updateCurrentUgcId,
  requestPreviousUgcIdUpdate,
} from './moderateActions';
import {
  updateObservations,
  requestObservationsUpdate,
} from 'ducks/coreDataActions';
import styles from './Moderate.scss';
import { bindActionCreators } from 'redux';
import ContentRequest from 'pages/__components__/ContentRequest';
import ContentResponse from 'pages/__components__/ContentResponse';
import { type AppConfigState } from 'ducks/appConfigReducer';
import { type ModerateState } from './moderateReducer';
import { type AuthState } from 'ducks/authReducer';
import { CoreDataState } from 'ducks/coreDataReducer';
import { type Product, ModerationState, Observation } from 'models';
import moderateSaga from './moderateSaga';
import moderateReducer from './moderateReducer';
import HideClientSelect from 'containers/client/HideClientSelectMenu';

import { injectSaga, injectReducer } from 'store';
injectSaga(moderateSaga);
injectReducer({ moderate: moderateReducer });

type ModeratePageProps = {
  contentRequestStatus: RequestStatus,
  observationRequestStatus: RequestStatus,
  moderationState: ?ModerationState,
  subject: Product,
  ugc: Ugc,
  requestContent: () => void,
  requestObservationsUpdate: ModerationState => void,
  requestPreviousUgcIdUpdate: () => void,
  updateCurrentUgcId: () => void,
  updateObservations: ModerationState => void,
  ugcId: UgcId,
  previousUgcId: UgcId,
  todaysCompletedCount: number,
  previousContentButtonShouldBeEnabled: boolean,
};

type ModeratePageState = {
  savedNextUgc: ?UgcId,
};

export class Moderate extends React.Component<
  ModeratePageProps,
  ModeratePageState
> {
  state = { savedNextUgc: null };

  componentDidMount() {
    this.props.requestContent();
  }

  handleSubmit = (...rest: *) => {
    if (this.state.savedNextUgc !== null) {
      this.props.requestObservationsUpdate(...rest);
      this.props.updateCurrentUgcId(this.state.savedNextUgc);
      this.setState({ savedNextUgc: null });
    } else {
      this.props.requestPreviousUgcIdUpdate();
      this.props.requestObservationsUpdate(...rest);
      this.props.requestContent();
    }
  };

  loadPreviousContent = () => {
    this.setState({
      savedNextUgc: this.props.ugcId,
    });
    this.props.updateCurrentUgcId(this.props.previousUgcId);
  };

  render() {
    const {
      contentRequestStatus,
      observationRequestStatus,
      subject: product,
      ugc,
      ugcId,
      todaysCompletedCount,
      requestContent,
      updateObservations,
      previousContentButtonShouldBeEnabled,
    } = this.props;

    const showingPreviousContent =
      this.props.previousUgcId === this.props.ugcId;
    const moderationState =
      showingPreviousContent && this.props.moderationState
        ? clearPreviousDecision(this.props.moderationState)
        : this.props.moderationState;

    return (
      <HideClientSelect>
        <div className={styles.moderate}>
          <div className="Moderate__StatusBar">
            <div className="Moderate__StatusBar__Left">
              <button
                className="Moderate__PreviousContentButton"
                disabled={!previousContentButtonShouldBeEnabled}
                onClick={this.loadPreviousContent}
                data-testid="back-button"
              >
                <i className="fa fa-chevron-left mr2" aria-hidden="true" />
                Previous Content
              </button>
            </div>
            <div className="Moderate__StatusBar__Right justify-between">
              <span className="mr3 mid-gray">
                <span
                  className="Moderate__TodayCount mr2"
                  data-testid="today-count-value"
                >
                  {todaysCompletedCount}
                </span>
                Today&#39;s Count
              </span>
              <a
                href="https://docs.google.com/document/d/1PBUQukXSYY3_QlEM3nWtA8-NE_L2gcMAlsbMmjgjcvw"
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline hover-dark-blue Moderate__GuidelinesLink"
              >
                <i
                  className="fa fa-external-link v-mid mr2"
                  aria-hidden="true"
                />
                <span className="Moderate__GuidelinesLink__Prefix">
                  Moderation{' '}
                </span>Guidelines
              </a>
            </div>
          </div>

          <ContentRequest requestStatus={contentRequestStatus}>
            <ContentResponse
              ugcId={ugcId}
              requestContent={requestContent}
              moderationState={moderationState}
            >
              {moderationState && (
                <div
                  key={moderationState.getId()}
                  className={styles.moderateCard}
                >
                  <PowerReviewsKeyboardEnchancer>
                    <PowerReviewsModerationCard
                      key={moderationState.getId()}
                      data-id={moderationState.getId()}
                      status={observationRequestStatus}
                      product={product}
                      ugc={ugc}
                      beforeSubmit={
                        ModerationState.completePowerReviewsModeration
                      }
                      moderationState={moderationState}
                      onSubmit={this.handleSubmit}
                      onChange={updateObservations}
                    />
                  </PowerReviewsKeyboardEnchancer>
                </div>
              )}
            </ContentResponse>
          </ContentRequest>
        </div>
      </HideClientSelect>
    );
  }
}

// We do this to enable the save button for previously mod'd content
function clearPreviousDecision(moderationState) {
  const prModeratorClientId = 1;
  const updater = mp =>
    mp
      .delete(Observation.PowerModerationCode)
      .delete(Observation.SecondOpinionCode);

  return moderationState
    .updateIn(['savedObservations', prModeratorClientId], updater)
    .updateIn(['currentObservations', prModeratorClientId], updater);
}

const mapStateToProps = (state: {
  appConfig: AppConfigState,
  moderate: ModerateState,
  auth: AuthState,
  coreData: CoreDataState,
}) => {
  const { appConfig, moderate: session, auth, coreData } = state;
  const ugcId = session.ugcId;
  const previousContentButtonShouldBeEnabled =
    session.previousUgcId !== ugcId &&
    session.updatePreviousContentRequestStatus === 'complete';

  return {
    previousContentButtonShouldBeEnabled,
    token: auth.token,
    todaysCompletedCount: session.getCompletedCount(),
    ugcId,
    previousUgcId: session.previousUgcId,
    ...CoreDataState.getTheThings(coreData, ugcId),
    observationsMap: appConfig.observationsMap,
    contentRequestStatus: session.contentRequestStatus,
  };
};

const mergeProps = (stateProps: *, { dispatch }, ownProps) => {
  const { token, observationsMap, ...restStateProps } = stateProps;

  return {
    ...ownProps,
    ...restStateProps,
    ...bindActionCreators(
      {
        updateObservations,
        requestObservationsUpdate,
        updateCurrentUgcId,
        requestPreviousUgcIdUpdate,
      },
      dispatch
    ),
    requestContent() {
      dispatch(requestContent({ token, observationsMap }));
    },
  };
};

// $FlowFixMe
export default connect(mapStateToProps, undefined, mergeProps)(Moderate);



// WEBPACK FOOTER //
// ./src/pages/moderation/moderate/Moderate.js